"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { WaterfallChart } from "@/src/components/charts/waterfall-chart";

export default function DocsWaterfallChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Waterfall Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Visualize cumulative effect of sequential positive and negative values.
              Perfect for P&L bridges, variance analysis, and cash flow walkthroughs.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>P&L Walkthrough</CardTitle>}
            contentClassName="p-theme-5"
          >
            <WaterfallChart
              data={{
                items: [
                  { name: "Revenue", value: 100000 },
                  { name: "COGS", value: -45000 },
                  { name: "Gross Profit", value: 0, isTotal: true },
                  { name: "OpEx", value: -25000 },
                  { name: "EBITDA", value: 0, isTotal: true },
                ],
              }}
              title="Income Statement Bridge"
              subtitle="FY2024 Financial Performance"
              height={350}
              showValues
            />
          </SectionCard>
        </Section>

        {/* Variants */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Budget vs Actual</CardTitle>}
              contentClassName="p-theme-5"
            >
              <WaterfallChart
                data={{
                  items: [
                    { name: "Budget", value: 80000 },
                    { name: "Favorable Variance", value: 8000 },
                    { name: "Unfavorable Variance", value: -3000 },
                    { name: "Actual", value: 0, isTotal: true },
                  ],
                }}
                positiveColor="#10b981"
                negativeColor="#ef4444"
                totalColor="#6366f1"
                height={300}
                showValues
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Cash Flow Bridge</CardTitle>}
              contentClassName="p-theme-5"
            >
              <WaterfallChart
                data={{
                  items: [
                    { name: "Opening Balance", value: 50000 },
                    { name: "Operating Inflows", value: 35000 },
                    { name: "Investing Outflows", value: -15000 },
                    { name: "Financing Outflows", value: -8000 },
                    { name: "Closing Balance", value: 0, isTotal: true },
                  ],
                }}
                height={300}
                showValues
              />
            </SectionCard>
          </div>
        </Section>

        {/* Variance Analysis */}
        <Section title="Variance Analysis">
          <SectionCard
            header={<CardTitle>Budget Performance Bridge</CardTitle>}
            contentClassName="p-theme-5"
          >
            <WaterfallChart
              data={{
                items: [
                  { name: "Sales Budget", value: 500000 },
                  { name: "Organic Growth", value: 45000 },
                  { name: "New Customers", value: 35000 },
                  { name: "Customer Churn", value: -12000 },
                  { name: "Pricing Adjustments", value: -8000 },
                  { name: "Actual Sales", value: 0, isTotal: true },
                ],
              }}
              title="Sales Forecast vs Actual"
              subtitle="Q4 2024 Performance"
              positiveColor="#3b82f6"
              negativeColor="#f87171"
              totalColor="#8b5cf6"
              height={350}
              showValues
            />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>WaterfallChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">WaterfallChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Data items with name and value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">280</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Display legend</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showValues</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Display values on bars</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">positiveColor</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Color for positive values</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">negativeColor</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Color for negative values</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">totalColor</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Color for total items</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">function</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Custom value formatter function</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Usage Examples */}
        <Section title="Usage Examples">
          <SectionCard
            header={<CardTitle>Code Examples</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`// Basic waterfall
<WaterfallChart
  data={{
    items: [
      { name: "Revenue", value: 100000 },
      { name: "COGS", value: -45000 },
      { name: "Gross Profit", value: 0, isTotal: true },
    ],
  }}
  showValues
/>

// With custom colors
<WaterfallChart
  data={{
    items: [
      { name: "Opening", value: 50000 },
      { name: "Inflows", value: 35000 },
      { name: "Outflows", value: -15000 },
      { name: "Closing", value: 0, isTotal: true },
    ],
  }}
  positiveColor="#10b981"
  negativeColor="#ef4444"
  totalColor="#3b82f6"
/>

// With value formatter
<WaterfallChart
  data={{
    items: [
      { name: "Budget", value: 500000 },
      { name: "Variance", value: 45000 },
      { name: "Actual", value: 0, isTotal: true },
    ],
  }}
  formatValue={(value) => \`\$\${(value / 1000).toFixed(0)}K\`}
  title="Sales Performance"
  height={350}
/>`}</code>
            </pre>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>WaterfallChartData</CardTitle>}
            contentClassName="p-theme-5"
          >
            <div className="space-y-theme-3 text-sm">
              <Text>
                Each waterfall chart item has the following structure:
              </Text>

              <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
                <code>{`interface WaterfallChartData {
  items: Array<{
    name: string;        // Label for the item
    value: number;       // Positive or negative value
    isTotal?: boolean;   // Mark as total/subtotal row
  }>;
}`}</code>
              </pre>

              <div className="space-y-theme-2">
                <div>
                  <p className="font-semibold text-xs">name</p>
                  <p className="text-muted-foreground text-xs">Display label for the waterfall item</p>
                </div>

                <div>
                  <p className="font-semibold text-xs">value</p>
                  <p className="text-muted-foreground text-xs">Numeric value. Positive values show increases, negative show decreases</p>
                </div>

                <div>
                  <p className="font-semibold text-xs">isTotal</p>
                  <p className="text-muted-foreground text-xs">When true, displays cumulative total with distinct styling</p>
                </div>
              </div>
            </div>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsWaterfallChartPage as WaterfallChartPage };
